import React, { useMemo, useState } from 'react';
import { Props } from './props';
import {
  useApiUrl,
  useCustomMutation,
  useGetLocale,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Card, Skeleton, Tag } from '@scalingworks/react-admin-ui';
import {
  ActionButton,
  AddingVariant,
  DetailRenderer,
  DetailRendererProps,
  Divider,
  Link,
  OrderCancellationModal,
  OrderFulfillmentModal,
  OrderLineProduct,
  OrderPriceSummary,
  PaymentMethodModal,
  ShowPageWrapper,
  SomethingWentWrong,
  VariantItem,
} from '~/components';
import { resourceNames } from '~/resources/resource-names';
import { OrderAssignCustomer, OrderStatusUpdate } from '~/components/Order';
import {
  formatFullName,
  dateFormatter,
  getOrderStatus,
  getOrderStatusTagColor,
  joinAddress,
  useDownload,
  getOrderFulfillmentSlot,
  getOrderActions,
  toCamelCaseWord,
  compactJoin,
  codeFormat,
  haveOngoingFulfillments,
  isPendingAcceptOrder,
  OrderFinalStatus,
} from '~/resources/helpers';
import { OrderStates, OrderStatus } from '~/config/types';
import { Action } from '~/components/ActionButton/props';
import head from 'lodash/head';
import startCase from 'lodash/startCase';
import camelCase from 'lodash/camelCase';
import {
  AddItemToOrderWithAddOnInput,
  AdminOrderProcess,
  CreateOrderLineInput,
  Fulfillment,
  FulfillmentMethod,
  FulfillmentMethodType,
  Order,
  ProcessOrderInput,
  ProcessOrderResult,
  ProductList,
  ProductListOptions,
  ProductVariant,
  SalesChannel,
} from '~/api';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import { useOrder, useProducts, useProcessOrder, useUpdateOrderInfo } from './hooks';
import { ResourceField } from '@scalingworks/refine-react-admin';
import { LinkToDetails } from '@scalingworks/refine-react-admin';
import { AiOutlinePlus } from 'react-icons/ai';
import { AddItemModal } from '../add-item';
import {
  DayFormat,
  DineInPaymentMethodCode,
  FullDateFormat,
  FullDateTimeFormat,
  isAcceptOrderEnabled,
} from '~/config/constant';
import { join } from 'lodash';
import { OrderPaymentSection } from './payments';
import { currentChannelToken } from '~/config/auth-provider';
import { UpdateOrderOutletModal } from '../update-outlet';
import { UpdateOrderRemarkModal } from '../update-remark';

// TODO: how much points to earn
// Pending Backend API

export const OrderShowPage: React.FC<Props> = (props) => {
  const { queryResult } = props;

  // ======================= HOOKS
  const notification = useNotification();
  const { data, isLoading, refetch: refetchOrder } = queryResult;
  const {
    id,
    code,
    state,
    customer,
    orderPlacedAt,
    shippingAddress,
    billingAddress,
    variants,
    dineInMetadata,
    customerSnapshot,
    fulfillments,
    customFields,
    payments = [],
    shippingLines,
    couponCodes,
    promotions,
    currencyCode = '',
    orderPlacementMetadata: orderMeta,
  } = data?.data || {};

  const orderFulfillment = head(fulfillments);
  const { id: customerId, firstName = '', lastName = '', phoneNumber = '' } = customer || {};
  const customerName =
    firstName || lastName ? formatFullName(firstName, lastName) : customerSnapshot?.name || '-';
  const customerContact = phoneNumber ? `(${phoneNumber})` : '';
  const {
    remark,
    cancellationRemark,
    giftFee,
    giftOption,
    pointAwarded,
    checkoutThrough = '',
    purchaseOutlet,
    shippingMethodCode,
    posAcceptedAt,
  } = customFields || {};
  const purchaseOutletName = purchaseOutlet?.name || '';
  const extraInfo = orderFulfillment?.additionalProperties;
  const deliveryMethod = customFields?.shippingMethodCode;
  const shippingInfo = head(shippingLines);
  const orderStatus = getOrderStatus(state as OrderStates) as OrderStatus;
  const productIds = uniq(variants?.items?.map((item: ProductVariant) => item.productId));
  const latestPayment = payments?.[payments.length - 1];
  const fulfillmentOngoing = haveOngoingFulfillments(fulfillments || []);
  const { acceptOrder, isLoading: updatingOrder } = useUpdateOrderInfo();

  // ======================= API
  const { data: products, isLoading: loadingProducts } = useProducts<ProductList>({
    variables: {
      options: {
        value: {
          filter: { id: { in: productIds } },
        } as ProductListOptions,
        type: 'ProductListOptions',
      },
    },
    queryOptions: { enabled: !isEmpty(productIds) },
  });

  const {
    data: lines,
    refetch: refetchLines,
    isLoading: loadingLines,
  } = useOrder<Order>({
    id: id!,
    queryOptions: { enabled: !isLoading && !!id },
  });
  const refetchAll = () => {
    refetchOrder();
    refetchLines();
  };

  const { addManualPaymentToOrder, addFulfillmentToOrder, isLoading: mutating } = useProcessOrder();

  // NOTE: custom removeDraftOrderLine with addon
  const { mutate: removeOrderLine } = useCustomMutation({
    mutationOptions: {
      onSettled: (result) => {
        if (!!result?.data?.id) {
          refetchAll();
        }
      },
    },
  });

  const { mutate: addItemToOrder } = useCustomMutation({
    mutationOptions: {
      onSettled: (data) => {
        if (data?.data) {
          setOpenAddItem(false);
          refetchAll();
        }
      },
    },
  });

  const { mutate: markAsDelivered } = useCustomMutation({
    mutationOptions: {
      onSettled: (result) => {
        if (!!result?.data?.id) {
          refetchAll();
        }
      },
    },
  });

  // ======================= HOOKS
  const t = useTranslate();
  const lang = useGetLocale()()?.toLowerCase() || 'en';
  const apiUrl = useApiUrl();
  const { downloadInvoice, downloadReceipt } = useDownload();

  // ======================= STATES
  // TODO: refactor, too many states, even perf still very fast now
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openCancel, setOpenCancel] = useState(false);
  const [openFulfillOrder, setOpenFulfillOrder] = useState(false);
  const [openAssignCustomer, setOpenAssignCustomer] = useState(false);
  const [openAddItem, setOpenAddItem] = useState(false);
  // NOTE: paymentMethod code
  const [openPaymentMethod, setOpenPaymentMethod] = useState(false);
  const [openUpdateOutlet, setOpenUpdateOutlet] = useState(false);
  const [openRemarkEdit, setOpenRemarkEdit] = useState(false);

  // ======================= VARIABLES
  const isDineInDraft =
    orderStatus === OrderStatus.Draft && deliveryMethod === FulfillmentMethodType.DineIn;
  const currentChannel = localStorage.getItem(currentChannelToken);
  const isPendingAccept = useMemo(() => {
    return isPendingAcceptOrder(data?.data);
  }, [data?.data, isLoading, isPendingAcceptOrder]);

  // ======================= EVENTS
  // Only applicable for draft dine in order
  const onAddLine = (raw: AddingVariant | VariantItem[]) => {
    const adding = raw as AddingVariant;
    if (!isDineInDraft) {
      // not suppose to happen
      console.warn(`Item is not Dine in & Draft`);
      return;
    }

    const productIds = Object.keys(adding);
    const orderLines: CreateOrderLineInput[] = [];

    for (const productId of productIds) {
      const { variantId, quantity, addon } = adding[productId];

      const line: CreateOrderLineInput = {
        item: { productVariantId: variantId, quantity },
        addons: addon?.map((extra) => ({
          modifierGroup: extra.groupName,
          modifierVariantIds: extra.modifierIds,
        })),
      };

      orderLines.push(line);
    }

    addItemToOrder({
      method: 'post',
      url: apiUrl,
      values: {},
      metaData: {
        fields: ['id'],
        operation: 'addItemToOrderWithAddOn',
        variables: {
          input: {
            value: {
              orderId: id,
              orderLines,
            } as AddItemToOrderWithAddOnInput,
            type: 'AddItemToOrderWithAddOnInput!',
          },
        },
      },
      errorNotification: {
        message: t('order.show.addItemFailed', {}, 'Failed to add item'),
        type: 'error',
      },
      successNotification: {
        message: t('order.show.addItemSuccess', {}, 'Item added'),
        type: 'success',
      },
    });
  };

  const onMarkAsDelivered = () => {
    markAsDelivered(
      {
        url: apiUrl,
        method: 'post',
        metaData: {
          fields: [
            {
              operation: 'Fulfillment',
              fields: ['id'],
              fragment: true,
            },
          ],
          operation: 'transitionFulfillmentToState',
          variables: {
            id: {
              type: 'ID!',
              value: head(fulfillments)?.id,
            },
            state: {
              type: 'String!',
              value: 'Delivered',
            },
          },
        },
        values: {},
        errorNotification: {
          message: t('order.notifications.delivered.failed'),
          type: 'error',
        },
        successNotification: {
          message: t('order.notifications.delivered.success'),
          type: 'success',
        },
      },
      {
        onSuccess: () => {
          // Temporary use delayed refetch due to micro services action
          setInterval(() => {
            refetchAll();
          }, 3000);
        },
      }
    );
  };

  const onDeleteLine = (lineId: string) => {
    removeOrderLine({
      method: 'post',
      url: apiUrl,
      values: {},
      metaData: {
        fields: [
          {
            operation: 'Order',
            fields: ['id'] as ResourceField<Order>,
            fragment: true,
          },
        ],
        operation: 'removeDraftOrderLineWithModifier',
        variables: {
          orderId: {
            value: id,
            type: 'ID!',
          },
          orderLineId: {
            value: lineId,
            type: 'ID!',
          },
        },
      },
      errorNotification: {
        message: t('order.show.removeFailed', {}, 'Failed to remove item'),
        type: 'error',
      },
      successNotification: {
        message: t('order.show.removeSuccess', {}, 'Item removed'),
        type: 'success',
      },
    });
  };

  const onArrangePayment = () => {
    if (deliveryMethod === FulfillmentMethodType.DineIn) {
      // TODO: get this from backend
      onAddManualPaymentToOrder(DineInPaymentMethodCode);
    } else {
      setOpenPaymentMethod(true);
    }
  };

  const onFulfillItem = () => {
    const orderLines = lines?.data?.lines || [];
    if (!shippingMethodCode || !lines || isEmpty(orderLines)) return;

    // TODO: check flag and see allow fulfill 1by1

    addFulfillmentToOrder({
      orderId: id as string,
      autoMarkPreparing: true,
      method: shippingMethodCode,
      lineInputs: orderLines.map((line) => {
        return {
          orderLineId: line.id,
          quantity: line.quantity,
        };
      }),
      t,
      mutateOptions: {
        onSuccess: (result) => {
          if (result.data?.__typename === 'Fulfillment') {
            notification?.open?.({
              message: t('order.details.fulfillmentCreated'),
              type: 'success',
            });

            refetchAll();
          }
        },
      },
    });
  };

  const onAddManualPaymentToOrder = (paymentMethodCode: string) => {
    addManualPaymentToOrder({
      t,
      orderId: id!,
      paymentMethodCode,
      mutateOptions: {
        onSuccess: (result) => {
          if (result.data?.value) {
            notification?.open?.({ message: t('order.details.paymentArranged'), type: 'success' });
            setOpenPaymentMethod(false);
          } else {
            notification?.open?.({
              message: t('order.details.paymentArrangeFailed'),
              type: 'error',
            });
          }
          refetchAll();
        },
        onError: (error) => {
          notification?.open?.({ message: t('order.details.paymentArrangeFailed'), type: 'error' });
        },
      },
    });
  };

  // ======================= VIEWS
  const actions: Action[] = getOrderActions({
    order: data?.data,
    t,
    options: {
      onArrangePayment,
      onFulfillItem,
      onDownloadInvoice: downloadInvoice,
      onPrintReceipt: (transactionId) => downloadReceipt(transactionId, true),
      onUpdateStoreOrSlot: () => setOpenUpdateOutlet(true),
      onUpdateRemark: () => setOpenRemarkEdit(true),
      onCancel: () => setOpenCancel(true),
    },
  });

  // Order without customer, allow options to link
  if (!customerId) {
    actions.push({
      label: 'Assign Customer',
      name: 'assign-customer',
      onAction: () => setOpenAssignCustomer(true),
    });
  }

  const renderFulfillmentKey = () => {
    if (isEmpty(fulfillments)) return '';

    const orderState = getOrderStatus(state as OrderStates);
    const stateKey = {
      [OrderStatus.Preparing]: 'order.create.readyToPickupOrShipped',
      [OrderStatus.Shipping]: 'order.create.markAsDelivered',
    };

    return stateKey[orderState as OrderStatus.Preparing | OrderStatus.Shipping] || '';
  };

  const fulfillmentRendererProps = (): DetailRendererProps => {
    let rendererProps: DetailRendererProps = { data: {} };
    const { tableCodeWithPrefix: table, adultPax, kidPax } = dineInMetadata || {};
    const { trackingCode = '' } = orderFulfillment || {};
    const { courier, driverName, driverContact } = extraInfo || {};
    const { date: slotDate, timeSlot } = orderMeta || {};
    const slotData = getOrderFulfillmentSlot(timeSlot || '', slotDate);

    if (deliveryMethod === FulfillmentMethodType.Pickup) {
      rendererProps = {
        loading: isLoading,
        resource: 'order',
        data: {
          pickupSlot: slotData,
          pickupAt: joinAddress(shippingAddress || {}),
        },
      };
    }

    if (deliveryMethod === FulfillmentMethodType.Delivery) {
      rendererProps = {
        loading: isLoading,
        resource: 'order',
        data: {
          deliverySlot: slotData,
          shippingAddress: shippingAddress ? joinAddress(shippingAddress) : '-',
          trackingCode,
          courier,
          driver: driverName,
          driverContact,
        },
      };
    }

    if (deliveryMethod === FulfillmentMethodType.DineIn) {
      rendererProps = {
        loading: isLoading,
        resource: 'order',
        data: {
          table,
          adultPax,
          kidPax,
        },
      };
    }

    return rendererProps;
  };

  if (!isLoading && !id) return <SomethingWentWrong />;
  return (
    <main className="overflow-y-scroll">
      {openPaymentMethod && (
        <PaymentMethodModal
          open
          setOpen={setOpenPaymentMethod}
          onConfirm={onAddManualPaymentToOrder}
        />
      )}
      {openAssignCustomer && id && (
        <OrderAssignCustomer
          open={openAssignCustomer}
          setOpen={setOpenAssignCustomer}
          orderId={id}
          onCompleted={() => refetchOrder()}
        />
      )}
      {openFulfillOrder && (
        <OrderFulfillmentModal
          open={openFulfillOrder}
          setOpen={setOpenFulfillOrder}
          isDineInDraft={isDineInDraft}
          fulfillments={fulfillments}
          loadingProducts={loadingProducts}
          onDeleteLine={onDeleteLine}
          products={products?.data?.items}
          lines={lines?.data.lines?.filter((line) => !line.isAddOn)}
          method={deliveryMethod as FulfillmentMethodType}
          fulfillmentHandlerCode={shippingInfo?.shippingMethod?.fulfillmentHandlerCode}
          onCompleted={() => refetchOrder()}
        />
      )}
      {openAddItem && (
        <AddItemModal
          open={openAddItem}
          setOpen={setOpenAddItem}
          title={t('order.create.addItems', {}, 'Add Items')}
          onAddItem={onAddLine}
        />
      )}
      {openCancel && (
        <OrderCancellationModal
          orderId={id as string}
          lines={lines?.data.lines?.filter((line) => !line.isAddOn)}
          open={openCancel}
          setOpen={setOpenCancel}
          onCompleted={() => refetchOrder()}
        />
      )}
      {openUpdate && id && (
        <OrderStatusUpdate
          open={openUpdate}
          setOpen={setOpenUpdate}
          initialStatus={orderStatus}
          orderDeliveryMethod={deliveryMethod as FulfillmentMethodType}
          orderId={id}
          onCompleted={() => refetchAll()}
        />
      )}
      <ShowPageWrapper
        resourceName={resourceNames.order}
        loading={isLoading}
        title={id ? <span>{codeFormat(code)}</span> : null}
        extra={
          isPendingAccept &&
          // NOTE: disable accept order for np
          isAcceptOrderEnabled && (
            <div className="flex flex-row w-full items-center justify-end">
              <Button
                variant="solid"
                className="w-1/4 bg-primary-500"
                loading={updatingOrder}
                onClick={() =>
                  id && acceptOrder(id, { t, mutateOptions: { onSuccess: refetchAll } })
                }
              >
                Accept Order
              </Button>
            </div>
          )
        }
      >
        <section className="space-y-4 xl:grid xl:grid-cols-10 xl:gap-4 xl:space-y-0">
          {/* LEFT */}
          <div className="xl:col-span-5">
            <div className="flex flex-col space-y-4">
              <Card className="z-0">
                <Card.Header className="font-bold" bordered>
                  <section className="flex flex-row items-center justify-between">
                    <h1>{t('common.general', undefined, 'General')}</h1>
                    <ActionButton
                      customTitle={t('common.action', undefined, 'ACTION').toUpperCase()}
                      actions={actions}
                    />
                  </section>
                </Card.Header>
                <Card.Body>
                  <DetailRenderer
                    loading={isLoading}
                    data={{
                      orderDate: orderPlacedAt,
                      ...(posAcceptedAt ? { posAcceptedAt } : {}),
                      // customer: compactJoin([customerName, customerContact], ' '),
                      customer: customer,
                      status: t(`order.status.${orderStatus.toLowerCase()}`).toUpperCase(),
                      fulfillmentMethod: deliveryMethod,
                      ...(!!orderMeta?.timeSlot ? { fulfillmentSlot: orderMeta?.timeSlot } : {}),
                      store: purchaseOutletName,
                      shippingAddress: shippingAddress ? joinAddress(shippingAddress) : '-',
                      billingAddress: billingAddress ? joinAddress(billingAddress) : '-',
                      remark,
                      ...(orderStatus === OrderStatus.Cancelled
                        ? { cancelReason: cancellationRemark }
                        : {}),
                      // packagingOption: giftOption?.label?.[lang],
                      pointsEarned: pointAwarded,
                      placedFrom: compactJoin([startCase(camelCase(checkoutThrough || ''))], ' '),
                      // ...(!isEmpty(latestPayment) ? { paymentMethod: latestPayment.method } : {}),
                    }}
                    resource={'order'}
                    render={{
                      orderDate: (date) => <span>{dateFormatter(date, FullDateTimeFormat)}</span>,
                      posAcceptedAt: (date) => (
                        <span>{dateFormatter(date, FullDateTimeFormat)}</span>
                      ),
                      status: (status: OrderStatus) => (
                        <Tag color={getOrderStatusTagColor(orderStatus)}>
                          {status?.toUpperCase()}
                        </Tag>
                      ),
                      fulfillmentMethod: (method) => <span>{toCamelCaseWord(method)}</span>,
                      remark: (text) =>
                        text ? <span className="font-semibold">"{text}"</span> : '',
                      customer: (customer) => (
                        <LinkToDetails
                          resourceId={customer?.id || ''}
                          resourceName={resourceNames.customer}
                        >
                          <Link>{compactJoin([customerName, customerContact], ' ')}</Link>
                        </LinkToDetails>
                      ),
                      // paymentMethod: (methodCode) => <span>{toCamelCaseWord(methodCode)}</span>,
                    }}
                  />
                </Card.Body>
              </Card>

              {!isEmpty(fulfillments) && (
                <Card>
                  <Card.Header
                    className="font-bold flex flex-row justify-between items-center"
                    bordered
                  >
                    {isLoading ? (
                      <Skeleton heightClass="h-5" width="narrow" />
                    ) : (
                      <h1>
                        {deliveryMethod
                          ? t(`order.create.fulfillmentMethod.${camelCase(deliveryMethod)}`)
                          : ''}
                      </h1>
                    )}
                    {fulfillmentOngoing && !!renderFulfillmentKey() && (
                      <Button
                        onClick={
                          getOrderStatus(state as OrderStates) === OrderStatus.Shipping
                            ? onMarkAsDelivered
                            : () => setOpenFulfillOrder(true)
                        }
                        size="sm"
                        className="text-md whitespace-nowrap border-primary-500"
                      >
                        <span className="text-primary-500">{t(renderFulfillmentKey())}</span>
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <DetailRenderer {...fulfillmentRendererProps()} />
                  </Card.Body>
                </Card>
              )}
            </div>
          </div>

          {/* RIGHT */}
          <div className="xl:col-span-5 space-y-4">
            {/* Order */}
            <Card className="z-0">
              <Card.Header className="font-bold" bordered>
                <section className="flex flex-row items-center justify-between">
                  <h1>{t('order.name', undefined, 'Order')}</h1>
                  {isDineInDraft && (
                    <Button
                      size="sm"
                      className="text-md whitespace-nowrap border-primary-500"
                      onClick={() => setOpenAddItem(true)}
                    >
                      <div className="flex flex-row items-center space-x-2">
                        <AiOutlinePlus className="!text-primary-500" size={20} />
                        <span className="text-primary-500">
                          {t('actions.addItems', undefined, 'Add Item')}
                        </span>
                      </div>
                    </Button>
                  )}
                </section>
              </Card.Header>
              <Card.Body>
                {!loadingProducts && !isEmpty(products?.data?.items) && (
                  <OrderLineProduct
                    canModify={isDineInDraft}
                    onDelete={onDeleteLine}
                    lineProducts={products?.data?.items}
                    lines={lines?.data.lines?.filter((line) => !line.isAddOn)}
                  />
                )}
                <OrderPriceSummary
                  order={data?.data}
                  lines={lines?.data?.lines}
                  loading={isLoading || loadingLines}
                />
              </Card.Body>
            </Card>

            <OrderPaymentSection
              payments={payments || []}
              isLoading={isLoading}
              refetch={refetchAll}
              currency={currencyCode}
              disableActions={OrderFinalStatus.includes(orderStatus as OrderStatus)}
            />
          </div>
        </section>

        {openUpdateOutlet && (
          <UpdateOrderOutletModal
            open={openUpdateOutlet}
            setOpen={setOpenUpdateOutlet}
            orderId={id || ''}
            fulfillmentMethod={shippingMethodCode as FulfillmentMethod}
            initialValue={{
              outletFulfillmentId: orderMeta?.outletFulfillmentId || '',
              purchaseOutletId: purchaseOutlet?.id || '',
            }}
            onSuccess={refetchOrder}
          />
        )}

        {openRemarkEdit && (
          <UpdateOrderRemarkModal
            open={openRemarkEdit}
            setOpen={setOpenRemarkEdit}
            orderId={id || ''}
            initialRemark={remark || undefined}
            onSuccess={refetchOrder}
          />
        )}
      </ShowPageWrapper>
    </main>
  );
};
